import React, { useState, useEffect } from 'react';
import Arrow from '../Icons/Arrow';
import { carSilhouetteLogo } from '../../../assets/images';
import ArrowLeftPagination from '../../atoms/Icons/ArrowLeftPagination';
import ArrowRightPagination from '../../atoms/Icons/ArrowRightPagination';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store';
import { VehicleDetails } from '../../../interface';
import Image from 'next/image';
import { CarSilhouette } from 'ui/assets/icons';
import { useRouter } from 'next/router';
import ArrowRight from '../Icons/ArrowRight';
import Link from 'next/link';

interface CarouselProps {
  carData: VehicleDetails[];
  autoSlide?: boolean;
  autoSlideInterval?: number;
}

const CustomCarousel: React.FC<CarouselProps> = ({
  carData,
  autoSlide = false,
  autoSlideInterval = 3000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isLargeScreen } = useSelector((state: IRootState) => state.page);
  const router = useRouter();

  // Handle auto sliding
  useEffect(() => {
    if (autoSlide) {
      const slideInterval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex === carData.length - 1 ? 0 : prevIndex + 1));
      }, autoSlideInterval);
      return () => clearInterval(slideInterval);
    }
  }, [autoSlide, autoSlideInterval, carData.length]);

  return (
    <div className="relative w-full max-w-2xl mx-auto py-5 bg-gradient-to-b from-[#F4F4F4]/0 to-[#F1F1F1]">
      <div className="overflow-hidden relative">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {carData.map((item, index) => {
            const image = item?.image;
            if (index !== 3) {
              return (
                <div key={index} className="min-w-full flex px-8 justify-between items-center">
                  <div>
                    <p className="font-bold lg:text-[16px] lg:leading-[20px] text-reliableBlack">
                      {item.carModel}
                    </p>
                    <p className="font-semibold lg:text-[12px] lg:leading-[16px] text-reliableBlack90">
                      {item.licensePlateNumber}
                    </p>
                    <p className="text-reliableBlack50 lg:text-[12px] lg:leading-[16px] font-normal">
                      {item?.stnkExpiryDate
                        ? moment(item.stnkExpiryDate).locale('id').format('MMM YYYY')
                        : ''}
                    </p>
                    <Link
                      className="cursor-pointer pt-4 flex items-center gap-1 text-supportiveRed"
                      href={`/myvehicles/details?licencePlate=${item.licensePlateNumber}`}
                    >
                      <p className="text-[10px] whitespace-nowrap font-semibold">LIHAT DETAIL</p>
                      <div className="size-4">
                        <Arrow direction="right" size={isLargeScreen ? 16 : 12} />
                      </div>
                    </Link>
                  </div>
                  <div>
                    <Image
                      src={image ? image.url : carSilhouetteLogo.src}
                      alt={`Slide ${index}`}
                      width={200}
                      height={140}
                    />
                    {item?.isCarTypeImage && (
                      <p className="text-supportiveRed text-[8px] font-medium text-right">
                        *Gambar hanya ilustrasi
                      </p>
                    )}
                  </div>
                </div>
              );
            } else {
              return (
                <div className="flex flex-col items-center py-5 gap-4 min-w-full">
                  <div className="relative w-[182px] h-[72px]">
                    <Image
                      alt="car-sihouette"
                      src={(CarSilhouette as any).src}
                      fill
                      className="object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    className="text-supportiveRed font-semibold text-xs flex items-center"
                    onClick={() => {
                      router.push('/myvehicles');
                    }}
                  >
                    Lihat Semua Mobil <ArrowRight size={20} fill="#CE181E" />
                  </button>
                </div>
              );
            }
          })}
        </div>
      </div>
      <button
        className={`${currentIndex === 0 ? 'bg-reliableBlack20' : 'bg-reliableBlack'} py-4 px-1 absolute top-[50%]`}
        onClick={() => {
          if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
          }
        }}
      >
        <ArrowLeftPagination size={13} />
      </button>
      <button
        className={`${currentIndex === carData.length - 1 ? 'bg-reliableBlack20' : 'bg-reliableBlack'} py-4 px-1 absolute top-[50%] right-0`}
        onClick={() => {
          if (currentIndex < carData.length - 1) {
            setCurrentIndex(currentIndex + 1);
          }
        }}
      >
        <ArrowRightPagination size={13} />
      </button>
      {/* Dots */}
      <div className="flex justify-center absolute bottom-3 w-full gap-1">
        {carData.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-1 ${currentIndex === index ? 'bg-black w-6' : 'bg-gray-300 w-1.5'}`}
          />
        ))}
      </div>
    </div>
  );
};

export default CustomCarousel;
